import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  ANDROID_APK_DOWNLOAD_URL,
  CROWDFUNDING_URL,
  IOS_APP_STORE_URL,
  WEB_AUTH_UI_ENABLED,
} from "@/config/publicConfig";
import { useAuthStore } from "@/store/useAuthStore";

export default function LandingPage() {
  const { t } = useTranslation();
  const { user, profile } = useAuthStore();

  const siteUrl = "https://bamabang.net";

  const faqOrder = [9, 10, 1, 2, 3, 4, 5, 6, 7, 8];
  const faqMainEntity = faqOrder.map((n) => ({
    "@type": "Question" as const,
    name: t(`faq.q${n}`),
    acceptedAnswer: {
      "@type": "Answer" as const,
      text: t(`faq.a${n}`),
    },
  }));

  const softwareApplication: Record<string, unknown> = {
    "@type": "SoftwareApplication",
    name: "BamaBang",
    alternateName: "你妈帮你找对象",
    applicationCategory: "SocialNetworkingApplication",
    operatingSystem: IOS_APP_STORE_URL
      ? "iOS, Android"
      : "Android; iOS (App Store listing pending)",
    description: t("seo.description"),
    url: `${siteUrl}/`,
    downloadUrl: ANDROID_APK_DOWNLOAD_URL,
    featureList: [
      "Parent-led or friend-led matchmaking accounts (paid by the parent/friend)",
      "Chat-room handover: paid parent account hands over a specific chat room to the child for free",
      "Real-time online matching that requires both parties to be online simultaneously",
      "30-day commitment challenge with bonus / fee-deduction rules",
      "Identity-verified, 18+ users only",
      "Apple In-App Purchases on iOS",
    ],
    publisher: {
      "@type": "Organization",
      name: "BamaBang",
      url: `${siteUrl}/`,
      logo: `${siteUrl}/favicon.png`,
    },
  };
  if (IOS_APP_STORE_URL) {
    softwareApplication.installUrl = IOS_APP_STORE_URL;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: t("seo.title"),
        url: `${siteUrl}/`,
        description: t("seo.description"),
      },
      {
        "@type": "Organization",
        name: "BamaBang",
        url: `${siteUrl}/`,
        logo: `${siteUrl}/favicon.png`,
        sameAs: [
          "https://twitter.com/parentsmatch",
          "https://facebook.com/parentsmatch",
        ],
      },
      softwareApplication,
      {
        "@type": "FAQPage",
        mainEntity: faqMainEntity,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex flex-col relative">
      <SeoHead
        path=""
        title={t("seo.title")}
        description={t("seo.description")}
      >
        <meta name="keywords" content={t("seo.keywords")} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </SeoHead>

      {/* 众筹横幅：仅官网；设置 VITE_CROWDFUNDING_URL 才显示，App 内不得出现 */}
      {CROWDFUNDING_URL && (
        <a
          href={CROWDFUNDING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center h-9 w-full bg-pink-600 text-white text-center text-xs sm:text-sm px-4 truncate hover:bg-pink-700 transition relative z-[60]"
        >
          {t("crowdfunding.text")} <span className="underline font-semibold">{t("crowdfunding.cta")} →</span>
        </a>
      )}

      {/* Top Navigation */}
      <header className={`absolute ${CROWDFUNDING_URL ? "top-9" : "top-0"} w-full p-4 flex justify-between items-center z-50`}>
        <div className="flex items-center">
          <img
            src="/favicon.png"
            alt={t("hero.logo_alt")}
            className="w-16 h-16 mr-2"
          />
          <span className="font-bold text-gray-900 text-xl hidden sm:block">BamaBang</span>
        </div>

        <div className="flex items-center gap-4">
          <LanguageSwitcher />

          {/* User Auth Status */}
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 hidden sm:block">
                {profile?.display_name || user.email}
              </span>
              <Link
                to="/app"
                className="px-4 py-2 bg-pink-600 text-white rounded-full text-sm font-bold shadow-md hover:bg-pink-700 transition"
              >
                {t("hero.web_version")}
              </Link>
            </div>
          ) : (
            WEB_AUTH_UI_ENABLED && (
              <Link
                to="/login"
                className="px-4 py-2 bg-gray-900 text-white rounded-full text-sm font-bold shadow-md hover:bg-gray-800 transition"
              >
                {t("auth.login")} / {t("auth.register")}
              </Link>
            )
          )}
        </div>
      </header>

      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 pt-32 pb-10">
        <div className="max-w-3xl text-center space-y-8">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
            {t("hero.geo_h1")}
          </h1>

          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 max-w-2xl mx-auto leading-snug">
            {t("hero.geo_h2")}
          </h2>

          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto whitespace-pre-line">
            {t("hero.description")}
          </p>

          {/* Buttons */}
          <div className="flex flex-col md:flex-row gap-4 justify-center mt-8">
            <a
              href={IOS_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-8 py-4 bg-black text-white rounded-xl shadow-lg hover:opacity-90 transition transform hover:-translate-y-1 no-underline w-full md:w-auto"
            >
              <span className="text-2xl mr-2">🍎</span>
              <div className="text-left">
                <div className="text-xs">{t("hero.download_ios_label", "Download on the")}</div>
                <div className="text-lg font-bold">App Store</div>
              </div>
            </a>

            <button
              disabled
              className="flex items-center justify-center px-8 py-4 bg-gray-400 text-white rounded-xl shadow-none cursor-not-allowed w-full md:w-auto"
            >
              <span className="text-2xl mr-2">🤖</span>
              <div className="text-left">
                <div className="text-xs">{t("download.android_coming_soon", "Coming soon")}</div>
                <div className="text-lg font-bold">Android</div>
              </div>
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 -mt-2">
            <Link to="/download" className="text-pink-600 font-medium hover:underline">
              {t("hero.download_page_more", "Full install guide & download page →")}
            </Link>
          </p>

          {WEB_AUTH_UI_ENABLED && (
            <div className="mt-8">
              <Link
                to="/app"
                className="text-pink-600 font-semibold hover:underline text-lg"
              >
                {t("hero.web_version")}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* How it works — parent/friend → child chat-room handover */}
      <div className="bg-pink-50/60 py-16 px-4 border-t border-pink-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-3">
            {t("howitworks.title")}
          </h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-10">
            {t("howitworks.subtitle")}
          </p>
          <ol className="grid md:grid-cols-4 gap-6 list-none">
            {[1, 2, 3, 4].map((n) => (
              <li
                key={n}
                className="bg-white p-6 rounded-2xl shadow-sm border border-pink-100"
              >
                <div className="text-pink-600 font-bold text-sm mb-2">
                  {t("howitworks.step_label", { defaultValue: "Step" })} {n}
                </div>
                <h3 className="font-bold text-base mb-1 text-gray-900">
                  {t(`howitworks.step${n}_title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`howitworks.step${n}_desc`)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Features Section (GEO Content) */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            {t("features.section_title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-6 rounded-2xl bg-pink-50">
            <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
            <h3 className="text-xl font-bold mb-2">
              {t("features.wisdom.title")}
            </h3>
            <p className="text-gray-600">{t("features.wisdom.desc")}</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-pink-50">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-bold mb-2">
              {t("features.challenge.title")}
            </h3>
            <p className="text-gray-600">{t("features.challenge.desc")}</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-pink-50">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-xl font-bold mb-2">
              {t("features.safety.title")}
            </h3>
            <p className="text-gray-600">{t("features.safety.desc")}</p>
          </div>
          </div>
        </div>
      </div>

      {/* Pricing Section (Dual Track) REMOVED FOR NOW */}
      {/*
      <div className="bg-white py-16 px-4 border-t border-gray-100">
        ...
      </div>
      */}


      {/* FAQ Section (GEO Goldmine) */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10">
            {t("faq.title")}
          </h2>
          <div className="space-y-6">
            {faqOrder.map((n) => (
              <div key={n} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-bold text-lg mb-2">
                  Q: {t(`faq.q${n}`)}
                </h3>
                <p className="text-gray-600">A: {t(`faq.a${n}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Footer — Stripe 合规 Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-8">
            {/* Brand */}
            <div className="md:max-w-xs">
              <div className="flex items-center gap-2 mb-3">
                <img
                  src="/favicon.png"
                  alt={t("hero.logo_alt")}
                  className="w-8 h-8 opacity-70"
                />
                <span className="text-white font-bold text-lg">BamaBang</span>
              </div>
              <p className="text-sm leading-relaxed">
                {t("footer.about_us")}
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col gap-2 text-sm">
              <span className="text-white font-semibold mb-1">Legal</span>
              <Link to="/terms" className="hover:text-white transition">
                {t("footer.terms")}
              </Link>
              <Link to="/privacy" className="hover:text-white transition">
                {t("footer.privacy")}
              </Link>
              <Link to="/refund" className="hover:text-white transition font-semibold text-pink-400 hover:text-pink-300">
                {t("footer.refund", "Refund & Cancellation Policy")}
              </Link>
            </div>

            {/* Support & Contact */}
            <div className="flex flex-col gap-2 text-sm">
              <span className="text-white font-semibold mb-1">{t("footer.contact_label", "Contact & Support")}</span>
              <Link to="/support" className="hover:text-white transition">
                {t("support.contact_title", "Contact Us")}
              </Link>

              <a href="mailto:bamabang.info@gmail.com" className="hover:text-white transition">
                📧 bamabang.info@gmail.com
              </a>
            </div>
          </div>

          {/* Divider + Bottom */}
          <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs">&copy; 2026 bamabang.net. All rights reserved.</p>
            <p className="text-xs">{t("footer.18_plus")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
